import React from "react";
import { LucideIcon, Users, Clock, Loader2, Save } from "lucide-react";
import { getCategoryConfig } from "./ProjectFormBasicInfo";
import { getDifficultyConfig } from "./ProjectFormDetails";

interface Section {
  id: string;
  label: string;
  icon: LucideIcon;
}

interface WatchedValues {
  title?: string;
  category?: string;
  description?: string;
  hourlyRate?: number;
  totalSlots?: number;
  estimatedHours?: string;
  difficulty?: string;
  responsibilities?: { description: string }[];
  skillsRequired?: { name: string }[];
}

interface ProjectFormSidebarProps {
  sections: Section[];
  activeSection: string;
  onSectionClick: (sectionId: string) => void;
  watchedValues: WatchedValues;
  isValid: boolean;
  isPending: boolean;
  isEditMode?: boolean;
  hasChanges?: boolean;
}

const ProjectFormSidebar: React.FC<ProjectFormSidebarProps> = ({
  sections,
  activeSection,
  onSectionClick,
  watchedValues,
  isValid,
  isPending,
  isEditMode = false,
  hasChanges = true,
}) => {
  const completionStatus = [
    {
      label: "Basic Info",
      complete:
        watchedValues.title &&
        watchedValues.category &&
        watchedValues.description,
    },
    {
      label: "Details",
      complete:
        watchedValues.hourlyRate &&
        watchedValues.totalSlots &&
        watchedValues.estimatedHours &&
        watchedValues.difficulty,
    },
    {
      label: "Responsibilities",
      complete: watchedValues.responsibilities?.some((r) => r.description),
    },
    {
      label: "Skills",
      complete: watchedValues.skillsRequired?.some((s) => s.name),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Navigation */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
        <h3 className="font-semibold text-gray-900 mb-3">Sections</h3>
        <nav className="space-y-1">
          {sections.map((section) => (
            <button
              key={section.id}
              type="button"
              onClick={() => onSectionClick(section.id)}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                activeSection === section.id
                  ? "bg-[#8B52FF]/10 text-[#8B52FF]"
                  : "text-gray-600 hover:bg-gray-50"
              }`}
            >
              <section.icon className="w-4 h-4" />
              {section.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Preview Card */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 sticky top-6">
        <h3 className="font-semibold text-gray-900 mb-4">Preview</h3>

        <div className="p-4 bg-gray-50 rounded-xl space-y-3">
          {watchedValues.category && (
            <span
              className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${
                getCategoryConfig(watchedValues.category).bg
              } ${getCategoryConfig(watchedValues.category).text}`}
            >
              {watchedValues.category.toUpperCase()}
            </span>
          )}

          <h4 className="font-semibold text-gray-900">
            {watchedValues.title || "Project Title"}
          </h4>

          <p className="text-sm text-gray-500 line-clamp-2">
            {watchedValues.description || "Project description..."}
          </p>

          <div className="flex items-center justify-between pt-2 border-t border-gray-200">
            <span className="text-lg font-bold text-gray-900">
              ${watchedValues.hourlyRate || 0}/hr
            </span>
            {watchedValues.difficulty && (
              <span
                className={`text-sm font-medium ${getDifficultyConfig(
                  watchedValues.difficulty
                )}`}
              >
                {watchedValues.difficulty}
              </span>
            )}
          </div>

          <div className="flex items-center gap-2 text-xs text-gray-500">
            <Users className="w-3.5 h-3.5" />
            <span>{watchedValues.totalSlots || 0} slots</span>
            <span>•</span>
            <Clock className="w-3.5 h-3.5" />
            <span>{watchedValues.estimatedHours || "N/A"}</span>
          </div>
        </div>

        {/* Status Indicators */}
        <div className="mt-4 space-y-2">
          {completionStatus.map((item) => (
            <div key={item.label} className="flex items-center gap-2 text-sm">
              <div
                className={`w-2 h-2 rounded-full ${
                  item.complete ? "bg-emerald-500" : "bg-gray-300"
                }`}
              />
              <span className="text-gray-600">{item.label}</span>
            </div>
          ))}
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isPending || !isValid || (isEditMode && !hasChanges)}
          className="w-full mt-6 py-3.5 bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] text-white font-semibold rounded-xl shadow-lg shadow-purple-500/25 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
        >
          {isPending ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              {isEditMode ? "Saving..." : "Creating..."}
            </>
          ) : (
            <>
              <Save className="w-5 h-5" />
              {isEditMode ? "Save Changes" : "Create Project"}
            </>
          )}
        </button>

        {isEditMode && !hasChanges && (
          <p className="text-xs text-gray-500 text-center mt-2">
            No changes to save
          </p>
        )}
      </div>
    </div>
  );
};

export default ProjectFormSidebar;
